import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Snackbar } from "@mui/material";
import { RiMailLine, RiMapPinLine, RiNotification3Line, RiPhoneLine, RiStickyNoteLine, RiTicket2Line } from "@remixicon/react";
import { getUserById, PHONE_ALIASES } from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import {
  ageFrom, ageLabel, headerChips, personName, studentMenuItems, TABS, tabCount, tabFromPath,
} from "../../utils/studentDetail";
import { dateTimeLines } from "../../utils/kitPresentation";
import { tokens } from "../../utils/figmaTokens";
import { useBreadcrumbName } from "../../components/Layout/BreadcrumbContext";
import { InfoItem, InfoRow, PageHead } from "../../components/kit/PageHead";
import LinkTabs from "../../components/kit/LinkTabs";
import AccountMenu from "../../components/kit/AccountMenu";
import AdjustBalanceDialog from "../../components/AdjustBalanceDialog/AdjustBalanceDialog";
import BanAccountDialog from "../../components/BanAccountDialog/BanAccountDialog";
import AddNoteDialog from "../../components/StaffNotes/AddNoteDialog";
import { useNotesCount } from "../../components/StaffNotes/NotesTab";
import { OpenTicketDialog } from "../Lessons/LessonActions";
import EditStudentDialog from "./EditStudentDialog";
import SendNotificationDialog from "./SendNotificationDialog";

const phoneOf = (u) => PHONE_ALIASES.map((f) => u?.[f]).find(Boolean) || "";

// The student page (044 D29–D36), drawn from Figma (047 D13, D14; 10697:16585): a white card
// holding the head, the tabs and the open tab. The tab comes from the URL and each tab loads
// alone through the outlet. Actions are today's permission matrix (D33); the server checks
// the two that change an account or reach a phone.
const StudentPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const userRole = user?.role || "";
  const location = useLocation();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [missing, setMissing] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const [dialog, setDialog] = useState(""); // "" | "edit" | "adjust" | "notify" | "ticket" | "disable" | "note"
  const [snack, setSnack] = useState(null); // { message, severity }
  // The Notes pill (059): recounted after every add or delete, here or in the tab.
  const notes = useNotesCount(id);

  const notify = useCallback((message, severity = "success") => setSnack({ message, severity }), []);

  // The top bar's "Students / name" (047 D11); "Not found" once the id is (D83).
  useBreadcrumbName(id, student ? student.name || "Unnamed student" : missing ? "Not found" : null);

  // getUserById is null-safe: null for a missing document AND for a failed read. A
  // refresh that fails keeps what is on screen (no flash, the open tab stays mounted);
  // only a first load can end in "Student not found.", which carries a Retry (D36).
  // The newest student wins: a slow read for the page Ctrl+K just left must not land here.
  const latest = useRef("");
  const load = useCallback(async () => {
    latest.current = id;
    const found = await getUserById(id);
    if (latest.current !== id) return;
    // Only a student (a parent is one too): a tutor's id here is "not found", not an
    // "Adult" student with the tutor's name (047 D68), as the tutor page does in reverse.
    const isStudent = found?.role === "student";
    if (isStudent) setStudent(found);
    setMissing(!isStudent);
  }, [id]);

  useEffect(() => {
    setStudent(null);
    setMissing(false);
    load();
  }, [load]);

  const referredBy = student?.referral?.referredBy;
  useEffect(() => {
    if (!referredBy) {
      setReferrer(null);
      return undefined;
    }
    let cancelled = false;
    getUserById(referredBy).then((u) => {
      if (!cancelled) setReferrer(u);
    });
    return () => {
      cancelled = true;
    };
  }, [referredBy]);

  if (!student) {
    return missing ? (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          Student not found.
        </Alert>
        <Button component={RouterLink} to="/students" sx={{ mt: 2 }}>Back to Students</Button>
      </Box>
    ) : (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const phone = phoneOf(student);
  // The head's second line (047, "Student page"); the first is email · phone · time zone.
  const meta = [
    student.gender,
    ageLabel(ageFrom(student.date_of_birth)),
    `Joined ${dateTimeLines(student.createdAt)?.date || "-"}`,
    referrer ? `Referred by ${personName(referrer)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  // The merged user, not the role string, so a Notifications grant (044 D5) shows Send
  // notification: staffSendNotification accepts the grant (047 D64).
  const can = (p) => hasPermission(user, p);
  const menuItems = studentMenuItems(can, student);
  const tab = tabFromPath(location.pathname, id);
  const closeDialog = () => setDialog("");
  const afterWrite = (message) => {
    closeDialog();
    load();
    if (message) notify(message);
  };

  return (
    <Box
      sx={{
        bgcolor: tokens.surface.default,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: "16px",
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 3, md: 4 },
      }}
    >
      <PageHead
        name={student.name || "Unnamed student"}
        colorKey={student.id}
        src={student.imageUri}
        chips={headerChips(student)}
        lines={
          (student.email || phone || student.timeZone) && (
            <InfoRow>
              {student.email && <InfoItem Icon={RiMailLine}>{student.email}</InfoItem>}
              {phone && <InfoItem Icon={RiPhoneLine}>{phone}</InfoItem>}
              {/* Figma's location slot: no location is stored, the time zone is. */}
              {student.timeZone && <InfoItem Icon={RiMapPinLine}>{student.timeZone}</InfoItem>}
            </InfoRow>
          )
        }
        meta={meta}
        actions={
          <>
            {can(permissions.EDIT_LESSONS) && (
              <Button size="large" variant="outlined" startIcon={<RiTicket2Line size={20} />} onClick={() => setDialog("ticket")}>
                Open ticket
              </Button>
            )}
            <Button size="large" variant="outlined" startIcon={<RiStickyNoteLine size={20} />} onClick={() => setDialog("note")}>
              Add note
            </Button>
            {can(permissions.SEND_NOTIFICATIONS) && (
              <Button size="large" variant="contained" startIcon={<RiNotification3Line size={20} />} onClick={() => setDialog("notify")}>
                Send notification
              </Button>
            )}
            <AccountMenu items={menuItems} disabled={student.disabled} onPick={setDialog} />
          </>
        }
      />

      <LinkTabs
        label="Student sections"
        value={tab}
        tabs={TABS.map((t) => ({
          value: t.path,
          label: t.label,
          to: t.path ? `/students/${id}/${t.path}` : `/students/${id}`,
          count: t.key === "notes" ? notes.count : tabCount(t.key, student),
        }))}
      />

      {/* Keyed on the student: each student mounts fresh tabs, whose reads carry no stale-answer guard. */}
      <Outlet
        key={student.id}
        context={{ student, refreshStudent: load, actingUser: user, userRole, notify, notesVersion: notes.version, onNotesChanged: notes.bump }}
      />

      <AddNoteDialog
        open={dialog === "note"}
        onClose={closeDialog}
        personUid={student.id}
        actingUser={user}
        contactAllowed
        onSaved={(message) => {
          notes.bump();
          notify(message);
        }}
      />
      <EditStudentDialog open={dialog === "edit"} onClose={closeDialog} student={student} actingUser={user} onSaved={() => afterWrite("Saved.")} />
      <AdjustBalanceDialog open={dialog === "adjust"} onClose={closeDialog} person={student} actingUser={user} onAdjusted={() => afterWrite()} />
      <BanAccountDialog open={dialog === "disable"} onClose={closeDialog} person={student} actingUser={user} onDone={() => afterWrite()} />
      <SendNotificationDialog open={dialog === "notify"} onClose={closeDialog} person={student} actingUser={user} onSent={(message) => notify(message)} />
      <OpenTicketDialog
        open={dialog === "ticket"}
        onClose={closeDialog}
        booking={null}
        userId={student.id}
        label=""
        personName={student.name || "this student"}
        actingUser={user}
        onDone={(message, ticketId) => {
          notify(message);
          if (ticketId) navigate(`/inbox/${ticketId}`);
        }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.severity || "success"} onClose={() => setSnack(null)}>{snack?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentPage;
